import { ReactNode, isValidElement } from 'react';

import { AppIcon } from '@backstage/core-components';
import BrushIcon from '@material-ui/icons/Brush';
import DashboardIcon from '@material-ui/icons/Dashboard';
import TableChartIcon from '@material-ui/icons/TableChart';

import { HomepageToolkitSpec } from './useHomepageToolkit';

export type ToolKitItem = {
  label: string;
  url: string;
  icon: ReactNode | string;
};

export const ICON_REGISTRY: Record<string, JSX.Element> = {
  ahp: (<img   src="/Abound-Logo.svg"
      alt="AHP"
      style={{ width: 24, height: 24, borderRadius:"50%" }}/>),
  airtable: <TableChartIcon />,
  api: <AppIcon id="kind:api" />,
  bitbucket: (
    <img
      src="/bitbucket-logo.svg"
      alt="Bitbucket"
      style={{ width: 24, height: 24 }}
    />
  ),
  catalog: <AppIcon id="catalog" />,
  chat: <AppIcon id="chat" />,
  confluence: (
    <img
      src="/confluence-logo.svg"
      alt="Confluence"
      style={{ width: 24, height: 24 }}
    />
  ),
  component: <AppIcon id="kind:component" />,
  dashboard: <DashboardIcon />,
  docs: <AppIcon id="docs" />,
  domain: <AppIcon id="kind:domain" />,
  email: <AppIcon id="email" />,
  figma: <BrushIcon />,
  github: <AppIcon id="github" />,
  group: <AppIcon id="kind:group" />,
  jira: (
    <img src="/jira-logo.svg" alt="Jira" style={{ width: 24, height: 24 }} />
  ),
  location: <AppIcon id="kind:location" />,
  resource: <AppIcon id="kind:resource" />,
  system: <AppIcon id="kind:system" />,
  techdocs: <AppIcon id="techdocs" />,
  template: <AppIcon id="kind:template" />,
  user: <AppIcon id="kind:user" />,
};

export function resolveIcon(
  icon?: ReactNode | string,
): JSX.Element | undefined {
  if (!icon) {
    return undefined;
  }
  if (isValidElement(icon)) {
    return icon;
  }
  if (typeof icon === 'string') {
    const key = icon.toLowerCase();
    return ICON_REGISTRY[key] ?? icon;
  }

  return undefined;
}

export function resolveLinksForUser(
  ownership: string[],
  config: HomepageToolkitSpec,
): ToolKitItem[] {
  if (!config.homepage?.toolkit) {
    return [];
  }
  const ownershipSet = new Set(ownership);
  const availableGroups = config.homepage.toolkit;

  const defaults =
    availableGroups.default?.map(link => ({
      label: link.label,
      url: link.url,
      icon: resolveIcon(link.icon?.toString()),
    })) ?? [];

  const groupLinks = Object.entries(config.homepage?.toolkit?.byGroup ?? {})
    .filter(([groupRef]) => ownershipSet.has(groupRef))
    .flatMap(([, links]) =>
      links.map(link => ({
        label: link.label,
        url: link.url,
        icon: resolveIcon(link.icon),
      })),
    );

  const merged = [...defaults, ...groupLinks];

  // Deduplicate
  const unique = new Map<string, ToolKitItem>();
  for (const item of merged) {
    const key = `${item.label}::${item.url}`;
    if (!unique.has(key)) {
      unique.set(key, item);
    }
  }

  return Array.from(unique.values());
}
